import type { Locale } from "../content/types";

export type PublicRatingsSummary = {
  host: string;
  product_key: string;
  rating_value: number | null;
  rating_count: number;
  best_rating: number;
  worst_rating: number;
  schema_eligible: boolean;
};

export type DynamicBooksPublicRatingsTarget = {
  host: "dynamicbooks.se" | "dynamicbooks.app";
  productKey: "dynamicbooks_software";
  product_key: "dynamicbooks_software";
  locale: "sv-SE" | "en-US";
};

export type PublicRatingsEndpoints = {
  summary: string;
  challenges: string;
  submissions: string;
};

export type DynamicBooksPublicRatingsBootstrap = {
  target: DynamicBooksPublicRatingsTarget;
  summary: PublicRatingsSummary;
  endpoints: PublicRatingsEndpoints;
};

export const DYNAMICBOOKS_SOFTWARE_RATING_TARGETS = {
  sv: {
    host: "dynamicbooks.se",
    productKey: "dynamicbooks_software",
    product_key: "dynamicbooks_software",
    locale: "sv-SE"
  },
  en: {
    host: "dynamicbooks.app",
    productKey: "dynamicbooks_software",
    product_key: "dynamicbooks_software",
    locale: "en-US"
  }
} as const satisfies Record<Locale, DynamicBooksPublicRatingsTarget>;

export const DYNAMICBOOKS_PUBLIC_RATINGS_ENDPOINTS = {
  summary: "/api/public/ratings/summary",
  challenges: "/api/public/ratings/challenges",
  submissions: "/api/public/ratings/submissions"
} as const satisfies PublicRatingsEndpoints;

declare global {
  // eslint-disable-next-line no-var
  var __DYNAMICBOOKS_PUBLIC_RATINGS__: DynamicBooksPublicRatingsBootstrap | undefined;
}

export function getDynamicBooksRatingsTarget(locale: Locale): DynamicBooksPublicRatingsTarget {
  return DYNAMICBOOKS_SOFTWARE_RATING_TARGETS[locale];
}

export function emptyPublicRatingsSummary(
  target: DynamicBooksPublicRatingsTarget = DYNAMICBOOKS_SOFTWARE_RATING_TARGETS.sv
): PublicRatingsSummary {
  return {
    host: target.host,
    product_key: target.product_key,
    rating_value: null,
    rating_count: 0,
    best_rating: 5,
    worst_rating: 1,
    schema_eligible: false
  };
}

export function normalizePublicRatingsSummary(
  value: unknown,
  target: DynamicBooksPublicRatingsTarget = DYNAMICBOOKS_SOFTWARE_RATING_TARGETS.sv
): PublicRatingsSummary {
  if (!isRecord(value)) {
    return emptyPublicRatingsSummary(target);
  }

  const ratingCount = readNonNegativeInteger(value.rating_count);
  const ratingValue = readRatingValue(value.rating_value, ratingCount);
  const summary = {
    host: readTargetString(value.host, target.host),
    product_key: readTargetString(value.product_key, target.product_key),
    rating_value: ratingValue,
    rating_count: ratingCount,
    best_rating: readBoundedRating(value.best_rating, 5),
    worst_rating: readBoundedRating(value.worst_rating, 1),
    schema_eligible: Boolean(value.schema_eligible) && ratingCount > 0 && ratingValue !== null
  };

  return isDynamicBooksSoftwareSummary(summary, target) ? summary : emptyPublicRatingsSummary(target);
}

export function isDynamicBooksSoftwareSummary(
  summary: PublicRatingsSummary,
  target: DynamicBooksPublicRatingsTarget = DYNAMICBOOKS_SOFTWARE_RATING_TARGETS.sv
): boolean {
  return summary.host === target.host && summary.product_key === target.product_key;
}

export function isPublicRatingsSummarySchemaEligible(
  value: unknown,
  target: DynamicBooksPublicRatingsTarget = DYNAMICBOOKS_SOFTWARE_RATING_TARGETS.sv
): value is PublicRatingsSummary {
  const summary = normalizePublicRatingsSummary(value, target);

  return (
    summary.schema_eligible &&
    summary.rating_count > 0 &&
    summary.rating_value !== null &&
    summary.rating_value >= summary.worst_rating &&
    summary.rating_value <= summary.best_rating
  );
}

export function getDynamicBooksPublicRatingsBootstrap(
  target: DynamicBooksPublicRatingsTarget = DYNAMICBOOKS_SOFTWARE_RATING_TARGETS.sv
): DynamicBooksPublicRatingsBootstrap | null {
  return normalizeDynamicBooksPublicRatingsBootstrap(globalThis.__DYNAMICBOOKS_PUBLIC_RATINGS__, target);
}

export function publishDynamicBooksPublicRatingsSummary(
  summary: PublicRatingsSummary,
  target: DynamicBooksPublicRatingsTarget = DYNAMICBOOKS_SOFTWARE_RATING_TARGETS.sv
): void {
  globalThis.__DYNAMICBOOKS_PUBLIC_RATINGS__ = {
    target,
    summary: normalizePublicRatingsSummary(summary, target),
    endpoints: DYNAMICBOOKS_PUBLIC_RATINGS_ENDPOINTS
  };
}

export function buildRatingsSummaryQuery(
  target: DynamicBooksPublicRatingsTarget,
  summaryEndpoint: string = DYNAMICBOOKS_PUBLIC_RATINGS_ENDPOINTS.summary
): string {
  const params = new URLSearchParams({
    host: target.host,
    product_key: target.product_key
  });

  return `${summaryEndpoint}?${params.toString()}`;
}

function normalizeDynamicBooksPublicRatingsBootstrap(
  value: unknown,
  target: DynamicBooksPublicRatingsTarget
): DynamicBooksPublicRatingsBootstrap | null {
  if (!isRecord(value) || !isRatingsTarget(value.target, target)) {
    return null;
  }

  return {
    target,
    summary: normalizePublicRatingsSummary(value.summary, target),
    endpoints: normalizePublicRatingsEndpoints(value.endpoints)
  };
}

function normalizePublicRatingsEndpoints(value: unknown): PublicRatingsEndpoints {
  if (!isRecord(value)) {
    return DYNAMICBOOKS_PUBLIC_RATINGS_ENDPOINTS;
  }

  return {
    summary: readEndpoint(value.summary, DYNAMICBOOKS_PUBLIC_RATINGS_ENDPOINTS.summary),
    challenges: readEndpoint(value.challenges, DYNAMICBOOKS_PUBLIC_RATINGS_ENDPOINTS.challenges),
    submissions: readEndpoint(value.submissions, DYNAMICBOOKS_PUBLIC_RATINGS_ENDPOINTS.submissions)
  };
}

function readEndpoint(value: unknown, fallback: string): string {
  return typeof value === "string" && value.startsWith("/api/public/ratings/") ? value : fallback;
}

function isRatingsTarget(value: unknown, target: DynamicBooksPublicRatingsTarget): boolean {
  return (
    isRecord(value) &&
    value.host === target.host &&
    value.product_key === target.product_key &&
    value.locale === target.locale
  );
}

function readTargetString(value: unknown, fallback: string): string {
  return typeof value === "string" && value.trim() ? value.trim().toLowerCase() : fallback;
}

function readNonNegativeInteger(value: unknown): number {
  const parsed = Number(value);

  return Number.isInteger(parsed) && parsed > 0 ? parsed : 0;
}

function readBoundedRating(value: unknown, fallback: number): number {
  const parsed = Number(value);

  return Number.isInteger(parsed) && parsed >= 1 && parsed <= 5 ? parsed : fallback;
}

function readRatingValue(value: unknown, ratingCount: number): number | null {
  if (ratingCount <= 0 || value === null || value === undefined) {
    return null;
  }

  const parsed = Number(value);

  if (!Number.isFinite(parsed) || parsed < 1 || parsed > 5) {
    return null;
  }

  return Math.round(parsed * 10) / 10;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value && typeof value === "object" && !Array.isArray(value));
}
